<template>
  <section class="contact-section">
    <div class="contact-container">
      <div class="contact-content">
        <div class="contact-header">
          <h2>Get in Touch</h2>
          <p>Let's discuss how we can help your business grow</p>
          <div class="contact-info">
            <a href="tel:+13313454999" class="contact-link">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              +1 (331) 345-4999
            </a>
            <a href="mailto:autogrield@gmail.com" class="contact-link">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              autogrield@gmail.com
            </a>
          </div>
        </div>
        <form @submit.prevent="handleSubmit" class="contact-form" autocomplete="off">
          <!-- Honeypot field — invisible to real users, bots will fill it -->
          <input
            type="text"
            v-model="honeypot"
            class="hp-field"
            tabindex="-1"
            autocomplete="off"
            aria-hidden="true"
          />

          <p v-if="errors.general" class="error-message general-error">{{ errors.general }}</p>

          <div class="form-grid">
            <!-- First Name -->
            <div class="form-group">
              <label for="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                v-model="form.firstName"
                required
                maxlength="100"
                :class="{ 'error': errors.firstName }"
                placeholder="Enter your first name"
              />
              <span v-if="errors.firstName" class="error-message">{{ errors.firstName }}</span>
            </div>

            <!-- Last Name -->
            <div class="form-group">
              <label for="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                v-model="form.lastName"
                required
                maxlength="100"
                :class="{ 'error': errors.lastName }"
                placeholder="Enter your last name"
              />
              <span v-if="errors.lastName" class="error-message">{{ errors.lastName }}</span>
            </div>

            <!-- Company Name -->
            <div class="form-group">
              <label for="companyName">Company Name *</label>
              <input
                type="text"
                id="companyName"
                v-model="form.companyName"
                required
                maxlength="100"
                :class="{ 'error': errors.companyName }"
                placeholder="Enter your company name"
              />
              <span v-if="errors.companyName" class="error-message">{{ errors.companyName }}</span>
            </div>

            <!-- Email -->
            <div class="form-group">
              <label for="email">Email *</label>
              <input
                type="email"
                id="email"
                v-model="form.email"
                required
                maxlength="254"
                :class="{ 'error': errors.email }"
                placeholder="Enter your email address"
              />
              <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
            </div>

            <!-- Phone -->
            <div class="form-group">
              <label for="phone">Phone # *</label>
              <input
                type="tel"
                id="phone"
                v-model="form.phone"
                required
                maxlength="20"
                :class="{ 'error': errors.phone }"
                placeholder="Enter your phone number"
              />
              <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
            </div>

            <!-- Fleet Size -->
            <div class="form-group">
              <label for="fleetSize">Fleet Size *</label>
              <select
                id="fleetSize"
                v-model="form.fleetSize"
                required
                :class="{ 'error': errors.fleetSize }"
              >
                <option value="" disabled>Select fleet size</option>
                <option value="1">1</option>
                <option value="2-9">2-9</option>
                <option value="10-19">10-19</option>
                <option value="20+">20+</option>
              </select>
              <span v-if="errors.fleetSize" class="error-message">{{ errors.fleetSize }}</span>
            </div>
          </div>

          <div class="submit-container">
            <button
              type="submit"
              :disabled="isSubmitting"
              class="submit-button"
            >
              <span v-if="isSubmitting" class="loading">
                <svg class="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
              <span v-else>Submit</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Thank You!</h3>
          <button class="modal-close" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <p>We'll get back to you soon!</p>
        </div>
        <div class="modal-footer">
          <button class="modal-button" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const ALLOWED_FLEET_SIZES = ['1', '2-9', '10-19', '20+']
const RATE_LIMIT_MS = 10000 // 10 seconds between submissions
const MAX_FIELD_LENGTH = 100
const MAX_EMAIL_LENGTH = 254

export default {
  name: 'ContactForm',
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        fleetSize: ''
      },
      // Honeypot field — hidden from real users, bots will fill it
      honeypot: '',
      errors: {},
      isSubmitting: false,
      showModal: false,
      lastSubmitTime: 0
    }
  },
  methods: {
    /**
     * Strips HTML tags, script content, and dangerous characters from input.
     * Prevents XSS and injection payloads from reaching the server.
     */
    sanitize(input) {
      if (typeof input !== 'string') return ''
      return input
        // Remove HTML tags and their content for script/style
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        // Remove all remaining HTML tags
        .replace(/<[^>]*>/g, '')
        // Remove common injection characters
        .replace(/[<>"'`;(){}[\]\\]/g, '')
        // Collapse multiple spaces
        .replace(/\s+/g, ' ')
        .trim()
    },
    validateForm() {
      this.errors = {}
      let isValid = true

      // Rate limiting check
      const now = Date.now()
      if (now - this.lastSubmitTime < RATE_LIMIT_MS) {
        this.errors.general = 'Please wait before submitting again'
        isValid = false
        return isValid
      }

      // Honeypot check — if filled, silently reject (likely a bot)
      if (this.honeypot) {
        // Fake success to not tip off the bot
        this.showModal = true
        return false
      }

      // First name validation
      const nameRegex = /^[a-zA-ZÀ-ÿа-яА-ЯёЁіІїЇєЄґҐ'\-\s]+$/
      const sanitizedFirstName = this.sanitize(this.form.firstName)
      if (!sanitizedFirstName || sanitizedFirstName.length < 1) {
        this.errors.firstName = 'First name is required'
        isValid = false
      } else if (sanitizedFirstName.length > MAX_FIELD_LENGTH) {
        this.errors.firstName = `First name must be under ${MAX_FIELD_LENGTH} characters`
        isValid = false
      } else if (!nameRegex.test(sanitizedFirstName)) {
        this.errors.firstName = 'First name contains invalid characters'
        isValid = false
      }

      // Last name validation
      const sanitizedLastName = this.sanitize(this.form.lastName)
      if (!sanitizedLastName || sanitizedLastName.length < 1) {
        this.errors.lastName = 'Last name is required'
        isValid = false
      } else if (sanitizedLastName.length > MAX_FIELD_LENGTH) {
        this.errors.lastName = `Last name must be under ${MAX_FIELD_LENGTH} characters`
        isValid = false
      } else if (!nameRegex.test(sanitizedLastName)) {
        this.errors.lastName = 'Last name contains invalid characters'
        isValid = false
      }

      // Company name validation
      const sanitizedCompany = this.sanitize(this.form.companyName)
      if (!sanitizedCompany || sanitizedCompany.length < 1) {
        this.errors.companyName = 'Company name is required'
        isValid = false
      } else if (sanitizedCompany.length > MAX_FIELD_LENGTH) {
        this.errors.companyName = `Company name must be under ${MAX_FIELD_LENGTH} characters`
        isValid = false
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const sanitizedEmail = this.form.email.trim()
      if (!sanitizedEmail || sanitizedEmail.length > MAX_EMAIL_LENGTH) {
        this.errors.email = 'Please enter a valid email address'
        isValid = false
      } else if (!emailRegex.test(sanitizedEmail)) {
        this.errors.email = 'Please enter a valid email address'
        isValid = false
      }

      // Phone validation — digits, spaces, dashes, parens, plus sign only
      const phoneRegex = /^[+]?[\d\s\-().]{7,20}$/
      const sanitizedPhone = this.form.phone.trim()
      if (!sanitizedPhone) {
        this.errors.phone = 'Phone number is required'
        isValid = false
      } else if (!phoneRegex.test(sanitizedPhone)) {
        this.errors.phone = 'Please enter a valid phone number'
        isValid = false
      }

      // Fleet size whitelist validation
      if (!ALLOWED_FLEET_SIZES.includes(this.form.fleetSize)) {
        this.errors.fleetSize = 'Please select a valid fleet size'
        isValid = false
      }

      return isValid
    },
    async handleSubmit() {
      if (!this.validateForm()) return

      this.isSubmitting = true
      this.lastSubmitTime = Date.now()

      try {
        // Get client IP with timeout and fallback
        let ipAddress = ''
        try {
          const ipResponse = await Promise.race([
            fetch('https://api.ipify.org?format=json'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('IP fetch timeout')), 5000))
          ])
          const ipData = await ipResponse.json()
          ipAddress = ipData?.ip || ''
        } catch (ipError) {
          // Continue without IP address
        }

        // Sanitize all fields before sending
        const payload = {
          first_name: this.sanitize(this.form.firstName).slice(0, MAX_FIELD_LENGTH),
          last_name: this.sanitize(this.form.lastName).slice(0, MAX_FIELD_LENGTH),
          email: this.form.email.trim().slice(0, MAX_EMAIL_LENGTH),
          phone_number: this.form.phone.trim().replace(/[^\d\s\-+().]/g, '').slice(0, 20),
          company_name: this.sanitize(this.form.companyName).slice(0, MAX_FIELD_LENGTH),
          fleet_size: ALLOWED_FLEET_SIZES.includes(this.form.fleetSize) ? this.form.fleetSize : '',
          ip: ipAddress,
          website_custom: 'sent from website'
        }

        const response = await fetch('https://autogrield.retool.com/url/contact-us-form', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          await response.json().catch(() => ({}))
        }
      } catch (error) {
        // Submission error — still show success per UX design
      } finally {
        // Always reset form and show success modal
        this.form = {
          firstName: '',
          lastName: '',
          companyName: '',
          email: '',
          phone: '',
          fleetSize: ''
        }
        this.honeypot = ''
        this.showModal = true
        this.isSubmitting = false
      }
    },
    closeModal() {
      this.showModal = false
    }
  }
}
</script>

<style scoped>
.contact-section {
  background: linear-gradient(rgba(17, 24, 39, 0.9), rgba(17, 24, 39, 0.95)), url('/public/map3.png');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  align-items: center;
}

.contact-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  width: 100%;
}

.contact-content {
  max-width: 900px;
  margin: 0 auto;
}

.contact-header {
  text-align: center;
  margin-bottom: 4rem;
}

.contact-header h2 {
  font-size: 3.5rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1.2;
}

.contact-header p {
  font-size: 1.25rem;
  color: #9ca3af;
  max-width: 600px;
  margin: 0 auto;
}

.contact-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
}

.contact-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #e5e7eb;
  text-decoration: none;
  font-size: 1.125rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  background: rgba(23, 23, 23, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.contact-link:hover {
  background: rgba(23, 23, 23, 0.8);
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.2);
}

.contact-link svg {
  width: 20px;
  height: 20px;
  color: #22c55e;
}

@media (min-width: 640px) {
  .contact-info {
    flex-direction: row;
    justify-content: center;
  }
}

.contact-form {
  background: rgba(23, 23, 23, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transform: translateY(0);
  transition: all 0.3s ease;
}

.contact-form:hover {
  transform: translateY(-5px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.2);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
}

.form-group {
  position: relative;
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #e5e7eb;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 1rem 1.25rem;
  font-size: 1rem;
  border: 2px solid rgba(75, 85, 99, 0.5);
  border-radius: 12px;
  background: rgba(17, 17, 17, 0.7);
  color: #ffffff;
  transition: all 0.3s ease;
}

.form-group input:hover,
.form-group select:hover {
  border-color: #404040;
  background: rgba(17, 17, 17, 0.9);
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #404040;
  box-shadow: 0 0 0 4px rgba(64, 64, 64, 0.2);
  background: rgba(17, 17, 17, 0.9);
}

.form-group input::placeholder {
  color: #9ca3af;
}

.form-group select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

/* Honeypot — completely hidden from humans */
.hp-field {
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}

.general-error {
  text-align: center;
  margin-bottom: 1rem;
}

.error {
  border-color: #ef4444 !important;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: block;
}

.submit-container {
  margin-top: 3rem;
  text-align: center;
}

.submit-button {
  background: linear-gradient(135deg, #262626 0%, #171717 100%);
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  padding: 1rem 3rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #171717 0%, #0a0a0a 100%);
  border-color: rgba(255, 255, 255, 0.2);
}

.submit-button:active {
  transform: translateY(0);
  background: linear-gradient(135deg, #0a0a0a 0%, #171717 100%);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.spinner {
  animation: spin 1s linear infinite;
  width: 1.25rem;
  height: 1.25rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .contact-section {
    padding: 4rem 0;
  }

  .contact-header h2 {
    font-size: 2.5rem;
  }

  .contact-form {
    padding: 2rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .submit-button {
    width: 100%;
  }
}

/* Animation for form elements */
.form-group {
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stagger animation for form groups */
.form-group:nth-child(1) { animation-delay: 0.1s; }
.form-group:nth-child(2) { animation-delay: 0.2s; }
.form-group:nth-child(3) { animation-delay: 0.3s; }
.form-group:nth-child(4) { animation-delay: 0.4s; }
.form-group:nth-child(5) { animation-delay: 0.5s; }
.form-group:nth-child(6) { animation-delay: 0.6s; }

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

.modal-content {
  background: rgba(23, 23, 23, 0.95);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease-out;
}

.modal-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #ffffff;
}

.modal-body {
  padding: 2rem;
  text-align: center;
}

.success-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  color: #22c55e;
  animation: scaleIn 0.3s ease-out;
}

.success-icon svg {
  width: 100%;
  height: 100%;
}

.modal-body p {
  color: #e5e7eb;
  font-size: 1.125rem;
  line-height: 1.5;
  margin: 0;
}

.modal-footer {
  padding: 1.5rem;
  text-align: right;
}

.modal-button {
  background: linear-gradient(135deg, #262626 0%, #171717 100%);
  color: white;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-button:hover {
  background: linear-gradient(135deg, #171717 0%, #0a0a0a 100%);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.modal-button:active {
  transform: translateY(0);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}
</style> 